import React from 'react';
import { Target, Clock, Eye, ArrowRight } from 'lucide-react';

const ContractCardsDemo = () => {
  return (
    <div className="contract-cards-section">
      <div className="contract-cards-section-header">
        <h2 className="contract-cards-section-title">Contract Management</h2>
        <p className="contract-cards-section-subtitle">
          Choose your contract management approach
        </p>
      </div>
      
      <div className="contract-cards-grid">
        {/* Fixed-Price Contract Card */}
        <div className="contract-card">
          <div className="contract-card-status active">Active</div>
          
          <div className="contract-card-header">
            <div className="contract-card-icon fixed-price">
              <Target size={24} />
            </div>
            <div>
              <h3 className="contract-card-title">Fixed-Price Contracts</h3>
              <p className="contract-card-description">
                Manage fixed-price contracts with comprehensive scope, deliverables, and milestone tracking. 
                Perfect for projects with defined requirements and clear deliverables.
              </p>
            </div>
          </div>
          
          <div className="contract-card-content">
            <ul className="contract-card-features">
              <li>Scope Tracking</li>
              <li>Milestone Management</li>
              <li>Deliverables</li>
              <li>Quality Gates</li>
              <li>Change Management</li>
            </ul>
            
            <div className="contract-card-metrics">
              <div className="contract-card-metric">
                <div className="contract-card-metric-value">12</div>
                <div className="contract-card-metric-label">Active Contracts</div>
              </div>
              <div className="contract-card-metric">
                <div className="contract-card-metric-value">$2.4M</div>
                <div className="contract-card-metric-label">Total Value</div>
              </div>
            </div>
            
            <div className="contract-card-progress">
              <div className="contract-card-progress-label">Overall Progress</div>
              <div className="contract-card-progress-bar">
                <div className="contract-card-progress-fill" style={{ width: '75%' }}></div>
              </div>
            </div>
          </div>
          
          <button className="contract-card-action">
            <Eye size={16} />
            View Fixed-Price Contracts
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Time & Materials Contract Card */}
        <div className="contract-card">
          <div className="contract-card-status active">Active</div>
          
          <div className="contract-card-header">
            <div className="contract-card-icon time-materials">
              <Clock size={24} />
            </div>
            <div>
              <h3 className="contract-card-title">Time & Materials Contracts</h3>
              <p className="contract-card-description">
                Manage T&M contracts with detailed hourly rates, time tracking, and cost management. 
                Ideal for ongoing services and projects with evolving requirements.
              </p>
            </div>
          </div>
          
          <div className="contract-card-content">
            <ul className="contract-card-features">
              <li>Time Tracking</li>
              <li>Hourly Rates</li>
              <li>Cost Management</li>
              <li>Material Costs</li>
              <li>Billing Rules</li>
            </ul>
            
            <div className="contract-card-metrics">
              <div className="contract-card-metric">
                <div className="contract-card-metric-value">8</div>
                <div className="contract-card-metric-label">Active Contracts</div>
              </div>
              <div className="contract-card-metric">
                <div className="contract-card-metric-value">1,200h</div>
                <div className="contract-card-metric-label">Hours Logged</div>
              </div>
            </div>
            
            <div className="contract-card-progress">
              <div className="contract-card-progress-label">Budget Utilization</div>
              <div className="contract-card-progress-bar">
                <div className="contract-card-progress-fill" style={{ width: '60%' }}></div>
              </div>
            </div>
          </div>
          
          <button className="contract-card-action">
            <Eye size={16} />
            View T&M Contracts
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Enhanced Fixed-Price Contract Card */}
        <div className="contract-card">
          <div className="contract-card-status active">Enhanced</div>
          
          <div className="contract-card-header">
            <div className="contract-card-icon fixed-price">
              <Target size={24} />
            </div>
            <div>
              <h3 className="contract-card-title">Enhanced Fixed-Price Contracts</h3>
              <p className="contract-card-description">
                Advanced fixed-price contract management with comprehensive tracking, 
                performance metrics, and risk management capabilities.
              </p>
            </div>
          </div>
          
          <div className="contract-card-content">
            <ul className="contract-card-features">
              <li>Advanced Scope Tracking</li>
              <li>Milestone Variance Analysis</li>
              <li>Change Request Management</li>
              <li>Performance Metrics</li>
              <li>Risk Assessment</li>
              <li>Quality Gates</li>
            </ul>
            
            <div className="contract-card-metrics">
              <div className="contract-card-metric">
                <div className="contract-card-metric-value">95%</div>
                <div className="contract-card-metric-label">Quality Score</div>
              </div>
              <div className="contract-card-metric">
                <div className="contract-card-metric-value">88%</div>
                <div className="contract-card-metric-label">Timeliness</div>
              </div>
            </div>
            
            <div className="contract-card-progress">
              <div className="contract-card-progress-label">Project Progress</div>
              <div className="contract-card-progress-bar">
                <div className="contract-card-progress-fill" style={{ width: '65%' }}></div>
              </div>
            </div>
          </div>
          
          <button className="contract-card-action">
            <Eye size={16} />
            View Enhanced Fixed-Price
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Enhanced Time & Materials Contract Card */}
        <div className="contract-card">
          <div className="contract-card-status active">Enhanced</div>
          
          <div className="contract-card-header">
            <div className="contract-card-icon time-materials">
              <Clock size={24} />
            </div>
            <div>
              <h3 className="contract-card-title">Enhanced Time & Materials</h3>
              <p className="contract-card-description">
                Advanced T&M contract management with detailed time tracking, 
                cost analysis, and performance monitoring capabilities.
              </p>
            </div>
          </div>
          
          <div className="contract-card-content">
            <ul className="contract-card-features">
              <li>Detailed Time Tracking</li>
              <li>Cost Analysis</li>
              <li>Team Productivity</li>
              <li>Budget Monitoring</li>
              <li>Performance Metrics</li>
              <li>Utilization Tracking</li>
            </ul>
            
            <div className="contract-card-metrics">
              <div className="contract-card-metric">
                <div className="contract-card-metric-value">85%</div>
                <div className="contract-card-metric-label">Utilization</div>
              </div>
              <div className="contract-card-metric">
                <div className="contract-card-metric-value">92%</div>
                <div className="contract-card-metric-label">Efficiency</div>
              </div>
            </div>
            
            <div className="contract-card-progress">
              <div className="contract-card-progress-label">Budget Utilization</div>
              <div className="contract-card-progress-bar">
                <div className="contract-card-progress-fill" style={{ width: '43%' }}></div>
              </div>
            </div>
          </div>
          
          <button className="contract-card-action">
            <Eye size={16} />
            View Enhanced T&M
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractCardsDemo;
